/**
 * Insights das contas: Instagram, Páginas do Facebook, TikTok, YouTube e
 * Páginas do LinkedIn.
 *
 * Cartões do período contra o período anterior, a série diária, os melhores
 * posts (Instagram, TikTok, YouTube) e o público (Instagram). Tudo lido do que
 * o coletor guardou; "Atualizar agora" vai à rede na hora.
 *
 * "—" é "a rede não informou", nunca zero (`shared/social-insights.ts`).
 */

import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangle, BarChart3, ExternalLink, Loader2, RefreshCw, TrendingDown, TrendingUp } from "lucide-react";
import { IconeRede } from "@/features/social/redes";
import { NETWORK_LABELS } from "@shared/social-publishing";
import { toast } from "sonner";
import { IG_MEDIA_METRICS, METRIC_LABELS, TEM_RANKING_DE_POSTS, formatarNumero, type RedeDeInsights } from "@shared/social-insights";

/** Por que os totais param antes de hoje, em cada rede. */
const AVISO_DE_ATRASO: Record<RedeDeInsights, string> = {
  instagram: "a Meta leva até 48h para fechar os números de um dia; o gráfico mostra também os dias ainda abertos.",
  facebook: "a Meta leva até 48h para fechar os números de um dia; o gráfico mostra também os dias ainda abertos.",
  youtube: "o YouTube Analytics fecha os números com 2 a 3 dias de atraso; o gráfico mostra também os dias ainda abertos.",
  tiktok: "o TikTok só informa os totais do perfil; cada ponto do gráfico é a foto do dia em que buscamos.",
  linkedin: "o LinkedIn pode levar até 48h para fechar os números de um dia.",
};

const PERIODOS = [
  { value: 7, label: "Últimos 7 dias" },
  { value: 28, label: "Últimos 28 dias" },
  { value: 90, label: "Últimos 90 dias" },
] as const;
type Dias = (typeof PERIODOS)[number]["value"];

const CORES = ["#ec4899", "#6366f1", "#22c55e"];

function Cartao({
  label,
  valor,
  parcial,
  variacao,
}: {
  label: string;
  valor: number | null;
  parcial: boolean;
  variacao: number | null;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 pt-5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold" title={valor == null ? "A rede não informou este número para o período" : undefined}>
          {formatarNumero(valor)}
        </p>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
          {variacao != null && (
            <span
              className={`inline-flex items-center gap-0.5 whitespace-nowrap ${variacao >= 0 ? "text-emerald-600" : "text-red-600"}`}
              title="Comparado ao período anterior de mesmo tamanho"
            >
              {variacao >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {variacao >= 0 ? "+" : "−"}
              {Math.abs(variacao).toFixed(0)}%
            </span>
          )}
          {valor == null && <span className="text-muted-foreground">Não informado pela rede</span>}
          {valor != null && parcial && <span className="text-amber-700 dark:text-amber-300" title="Faltou o número de algum dia do período">parcial</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function Barras({ titulo, itens }: { titulo: string; itens: Array<{ chave: string; valor: number }> }) {
  const max = Math.max(1, ...itens.map((i) => i.valor));
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{titulo}</p>
      {itens.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sem dados.</p>
      ) : (
        itens.slice(0, 8).map((i) => (
          <div key={i.chave} className="flex items-center gap-2 text-xs">
            <span className="w-28 truncate" title={i.chave}>{i.chave}</span>
            <div className="h-2 flex-1 rounded bg-muted">
              <div className="h-2 rounded bg-pink-500" style={{ width: `${(i.valor / max) * 100}%` }} />
            </div>
            <span className="w-12 text-right tabular-nums">{formatarNumero(i.valor)}</span>
          </div>
        ))
      )}
    </div>
  );
}

function SocialInsightsContent() {
  const utils = trpc.useUtils();
  const contas = trpc.social.insights.accounts.useQuery();
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const [dias, setDias] = useState<Dias>(28);
  const [ordenarPor, setOrdenarPor] = useState<(typeof IG_MEDIA_METRICS)[number]>("views");

  useEffect(() => {
    if (!selecionada && contas.data?.length) {
      // Abre numa conta que tem números (perfil pessoal do LinkedIn não tem).
      const c = contas.data.find((x) => !("semMetricas" in x && x.semMetricas)) ?? contas.data[0];
      setSelecionada(`${c.network}:${c.id}`);
    }
  }, [contas.data, selecionada]);

  const conta = useMemo(
    () => contas.data?.find((c) => `${c.network}:${c.id}` === selecionada) ?? null,
    [contas.data, selecionada],
  );
  const ref = conta ? { network: conta.network, accountId: conta.id } : null;

  const overview = trpc.social.insights.overview.useQuery({ ...(ref ?? { network: "instagram", accountId: 0 }), dias }, { enabled: !!ref });
  // Cada rede ordena por métricas diferentes: volta para visualizações ao trocar de conta.
  useEffect(() => setOrdenarPor("views"), [selecionada]);
  const temRanking = conta ? TEM_RANKING_DE_POSTS[conta.network] : false;
  const ehIg = conta?.network === "instagram";
  const metricasDoPost = ehIg ? (["views", "reach", "total_interactions"] as const) : (["views", "likes", "comments"] as const);
  // O YouTube não informa compartilhamentos por vídeo.
  const ordenaveis = ehIg
    ? IG_MEDIA_METRICS
    : conta?.network === "youtube"
      ? (["views", "likes", "comments"] as const)
      : (["views", "likes", "comments", "shares"] as const);
  const semMetricas = !!conta && "semMetricas" in conta && !!conta.semMetricas;
  const posts = trpc.social.insights.posts.useQuery(
    { network: conta?.network ?? "instagram", accountId: conta?.id ?? 0, dias, ordenarPor, limit: 12 },
    { enabled: temRanking },
  );
  const publico = trpc.social.insights.demographics.useQuery(
    { accountId: conta?.id ?? 0 },
    { enabled: conta?.network === "instagram", staleTime: 60 * 60_000 },
  );

  const atualizar = trpc.social.insights.refresh.useMutation({
    onSuccess: () => {
      void utils.social.insights.invalidate();
      toast.success("Números atualizados.");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // Conta que nunca foi coletada: busca na primeira abertura, sem esperar a rodada de 6h.
  useEffect(() => {
    if (ref && !semMetricas && overview.data?.semDados && !overview.data.atualizadoEm && !atualizar.isPending && !overview.data.erro) {
      atualizar.mutate(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview.data?.semDados, selecionada]);

  if (contas.isFetched && (contas.data?.length ?? 0) === 0) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <BarChart3 className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="mt-4 text-lg font-semibold">Nenhuma conta conectada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Conecte o Instagram ou uma Página do Facebook em <strong>Configurações → Integrações</strong>, e o TikTok, o
          YouTube ou o LinkedIn em <strong>Redes sociais → Contas</strong>.
        </p>
      </div>
    );
  }

  const d = overview.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <BarChart3 className="h-6 w-6" /> Insights
          </h1>
          <p className="text-sm text-muted-foreground">Alcance, visualizações e seguidores das suas contas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selecionada ?? ""} onValueChange={setSelecionada}>
            <SelectTrigger className="w-[230px]" aria-label="Conta">
              <SelectValue placeholder="Escolha a conta" />
            </SelectTrigger>
            <SelectContent>
              {(contas.data ?? []).map((c) => (
                <SelectItem key={`${c.network}:${c.id}`} value={`${c.network}:${c.id}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <IconeRede rede={c.network} className="h-3.5 w-3.5" />
                    {c.nome}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(dias)} onValueChange={(v) => setDias(Number(v) as Dias)}>
            <SelectTrigger className="w-[170px]" aria-label="Período">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODOS.map((p) => (
                <SelectItem key={p.value} value={String(p.value)}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => ref && atualizar.mutate(ref)} disabled={!ref || atualizar.isPending || semMetricas}>
            {atualizar.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Atualizar agora
          </Button>
        </div>
      </div>

      {conta && "semMetricas" in conta && conta.semMetricas && (
        <p className="flex items-start gap-2 rounded border bg-muted/40 p-3 text-sm text-muted-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          O LinkedIn não libera os números de perfil pessoal para outros aplicativos — só das Páginas de empresa. Os
          cliques e leads dos seus posts aparecem em Leads das redes.
        </p>
      )}
      {conta?.semPermissao && (
        <p className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Esta conta foi conectada sem a permissão de ler insights. Reconecte em Configurações → Integrações marcando
          essa permissão na tela do Facebook.
        </p>
      )}
      {d?.erro && (
        <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          A última atualização falhou: {d.erro}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {d?.atualizadoEm
          ? `Atualizado em ${new Date(d.atualizadoEm).toLocaleString("pt-BR")}. Os totais vão até ${new Date(`${d.cartoesAte}T12:00:00Z`).toLocaleDateString("pt-BR")} — ${AVISO_DE_ATRASO[conta?.network ?? "instagram"]}`
          : atualizar.isPending
            ? `Buscando os números no ${NETWORK_LABELS[conta?.network ?? "instagram"]} pela primeira vez…`
            : "Ainda sem números desta conta."}
      </p>

      {!semMetricas && (
      <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {(d?.cartoes ?? []).map((c) => (
          <Cartao key={c.metric} label={c.label} valor={c.atual.valor} parcial={c.atual.parcial} variacao={c.variacao} />
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dia a dia</CardTitle>
        </CardHeader>
        <CardContent>
          {!d || d.semDados ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Sem números no período.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={d.serie}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tickFormatter={(v: string) => v.slice(8, 10) + "/" + v.slice(5, 7)} fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v: number) => formatarNumero(v)} />
                <Tooltip
                  formatter={(v: number) => formatarNumero(Number(v))}
                  labelFormatter={(l: string) => new Date(`${l}T12:00:00Z`).toLocaleDateString("pt-BR")}
                />
                <Legend />
                {d.metricasDaSerie.map((m, i) => (
                  // connectNulls=false: dia sem número fica em branco, não desce a zero.
                  <Line key={m.metric} type="monotone" dataKey={m.metric} name={m.label} stroke={CORES[i]} dot={false} connectNulls={false} strokeWidth={2} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      </>
      )}

      {temRanking && (
        <div className={`grid gap-4 ${ehIg ? "lg:grid-cols-[1fr_320px]" : ""}`}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">Melhores posts do período</CardTitle>
              <Select value={ordenarPor} onValueChange={(v) => setOrdenarPor(v as typeof ordenarPor)}>
                <SelectTrigger className="w-[180px]" aria-label="Ordenar por">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ordenaveis.map((m) => (
                    <SelectItem key={m} value={m}>
                      {METRIC_LABELS[m] ?? m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {posts.data?.length === 0 && <p className="text-sm text-muted-foreground">Nenhum post no período.</p>}
              <div className="divide-y">
                {(posts.data ?? []).map((p) => (
                  <div key={p.mediaId} className="flex items-center gap-3 py-2">
                    {p.thumbnailUrl ? (
                      <img src={p.thumbnailUrl} alt="" className="h-12 w-12 shrink-0 rounded object-cover" loading="lazy" />
                    ) : (
                      <div className="h-12 w-12 shrink-0 rounded bg-muted" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{p.legenda || "Sem legenda"}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.tipo} · {p.postadoEm ? new Date(p.postadoEm).toLocaleDateString("pt-BR") : ""}
                      </p>
                    </div>
                    <div className="grid shrink-0 grid-cols-3 gap-3 text-right text-xs tabular-nums">
                      {metricasDoPost.map((m) => (
                        <div key={m}>
                          <p className="font-medium">{formatarNumero(p.metricas[m] ?? null)}</p>
                          <p className="text-muted-foreground">{METRIC_LABELS[m]}</p>
                        </div>
                      ))}
                    </div>
                    {p.permalink && (
                      <a href={p.permalink} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" aria-label={`Abrir no ${NETWORK_LABELS[conta?.network ?? "instagram"]}`}>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {ehIg && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quem segue a conta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {publico.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
              {publico.data &&
              (publico.data.dados.idade?.length ?? 0) + (publico.data.dados.cidade?.length ?? 0) + (publico.data.dados.pais?.length ?? 0) > 0 ? (
                <>
                  <Barras titulo="Idade" itens={publico.data.dados.idade ?? []} />
                  <Barras titulo="Gênero" itens={(publico.data.dados.genero ?? []).map((g) => ({ ...g, chave: g.chave === "F" ? "Feminino" : g.chave === "M" ? "Masculino" : "Não informado" }))} />
                  <Barras titulo="Cidades" itens={publico.data.dados.cidade ?? []} />
                  <Barras titulo="Países" itens={publico.data.dados.pais ?? []} />
                </>
              ) : (
                !publico.isLoading && (
                  <p className="text-sm text-muted-foreground">
                    A Meta só mostra o público de contas com pelo menos 100 seguidores.
                  </p>
                )
              )}
            </CardContent>
          </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default function SocialInsights() {
  return (
    <PermissionGuard permission="social:view_insights">
      <DashboardLayout>
        <SocialInsightsContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
